import React, { useRef } from 'react';
import { Card, Button } from 'reactstrap';
import { useReactToPrint } from 'react-to-print';
import { useTenant } from '../../../hooks/useTenant';
import { ContentLoader, Breadcrumb } from '../../../components/Common';

import { useCashier } from './hooks/useCashier';
import { CashierTable } from './components/CashierTable';
import OpenCashierModal from './components/OpenCashierModal';
import CloseCashierModal from './components/CloseCashierModal';
import PrintableCashierReport from './components/PrintableCashierReport';

const CashierPage = () => {
    document.title = "Caixa | NeoApp - Sistema de Gestão";

    const { tenantName, tenantLogo, tenantSettings, user } = useTenant();
    const printRef = useRef();

    const {
        selectedDate,
        summary,
        records,
        loading,
        currentShift,
        modals,
        toggleOpenModal,
        toggleCloseModal,
        isProcessingShift,
        handleOpenShift,
        handleCloseShift
    } = useCashier();

    const handlePrint = useReactToPrint({
        contentRef: printRef,
        content: () => printRef.current,
        documentTitle: `Fechamento_Caixa_${selectedDate}`,
    });

    return (
        <React.Fragment>
            <div className="container-fluid p-0">
                {/* ═══ HEADER ═══ */}
                <div className="d-flex align-items-center justify-content-between mb-3">
                    <Breadcrumb title="Caixa" />
                    <div className="d-flex align-items-center gap-3">
                        <Button
                            color="primary"
                            className="d-flex align-items-center justify-content-center rounded-0 shadow-sm p-0 border-0"
                            style={{ width: '38px', height: '38px' }}
                            onClick={handlePrint}
                            title="Imprimir Fechamento de Caixa"
                        >
                            <i className="mdi mdi-printer font-size-18" />
                        </Button>
                        <Button
                            color={currentShift ? "danger" : "primary"}
                            className="fw-semibold shadow-sm rounded-0"
                            style={{ fontSize: '0.85rem', padding: '0.5rem 1rem' }}
                            onClick={currentShift ? toggleCloseModal : toggleOpenModal}
                        >
                            {currentShift ? "FECHAR CAIXA" : "ABRIR CAIXA"}
                        </Button>
                    </div>
                </div>

                <div className="border-top mb-3"></div>

                {/* ═══ PRINTABLE REPORT (HIDDEN) ═══ */}
                <div style={{ display: 'none' }}>
                    <div ref={printRef}>
                        <PrintableCashierReport
                            currentShift={currentShift}
                            summary={summary}
                            records={records}
                            user={user}
                            tenantName={tenantName || tenantSettings?.name}
                            tenantLogo={tenantLogo || tenantSettings?.logo}
                        />
                    </div>
                </div>

                {/* ═══ CAIXA ACCORDION & TABLE ═══ */}
                {loading && !currentShift ? (
                    <ContentLoader loading={true} message="Carregando dados do caixa..." />
                ) : !currentShift ? (
                    <Card className="border-0 shadow-sm p-5 text-center">
                        <i className="mdi mdi-cash-register text-muted" style={{ fontSize: '4rem' }}></i>
                        <h4 className="mt-3 text-dark fw-semibold">Nenhum caixa aberto encontrado</h4>
                        <p className="text-muted mb-4">Abra o caixa para começar a registrar ou visualizar as movimentações deste turno.</p>
                        <div>
                            <Button color="primary" onClick={toggleOpenModal} className="fw-semibold px-4 py-2 shadow-sm rounded-0">
                                Abrir Caixa Agora
                            </Button>
                        </div>
                    </Card>
                ) : (
                    <CashierTable
                        records={records}
                        loading={loading}
                        summary={summary}
                        selectedDate={selectedDate}
                        currentShift={currentShift}
                        onCloseCashier={toggleCloseModal}
                    />
                )}

                {/* MODALS */}
                <OpenCashierModal
                    isOpen={modals.open}
                    toggle={toggleOpenModal}
                    onConfirm={handleOpenShift}
                    isProcessing={isProcessingShift}
                />

                <CloseCashierModal
                    isOpen={modals.close}
                    toggle={toggleCloseModal}
                    onConfirm={handleCloseShift}
                    isProcessing={isProcessingShift}
                />
            </div>
        </React.Fragment>
    );
};

export default CashierPage;

